import { ChangeDetectorRef, Component, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ApiSettings, LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { PaymentHistoryListViewLocations } from '../../../../core/enums/base.enums';
import { PaymentsPermission } from '../../../../core/enums/permissions.enums';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { CurrencyModel } from '../../../../core/models/data/currency.model';
import { PaymentBulkActionRequest } from '../../../../core/models/service/payment-bulk-action-request.model';
import { PaymentBulkSummaryModel } from '../../../../core/models/data/payment-bulk-summary.model';
import { PaymentService } from '../../../../core/services/payment/payment.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { FileDownloadUtilities } from '../../../../core/utilities/file-download.utilities';
import { DateUtilities } from '../../../../core/utilities/date.utilities';
import { PaymentHistoryListComponent } from '../../components/payment-history-list/payment-history-list.component';
import { PaymentHistorySummaryCardsComponent } from '../../components/payment-history-summary-cards/payment-history-summary-cards.component';
import { PaymentHistorySearchFilter } from '../../components/payment-history-search-filter/payment-history-search-filter';
import { CloseMenuOnSettleDirective } from '../../../../core/directives/close-menu-on-settle.directive';
import { DebounceClickDirective } from '../../../../core/directives/debounce-click.directive';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-payment-history-page',
  imports: [
    PageTitleComponent,
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    PaymentHistoryListComponent,
    PaymentHistorySummaryCardsComponent,
    PaymentHistorySearchFilter,
    SortDropdownComponent,
    CloseMenuOnSettleDirective,
    DebounceClickDirective,
    IfHasPermissionDirective
  ],
  templateUrl: './payment-history.page.html'
})
export class PaymentHistoryPage implements OnInit {

  public PaymentHistoryListViewLocations = PaymentHistoryListViewLocations;
  public PaymentsPermission = PaymentsPermission;

  public sortOptions = ListConstants.PAYMENT_HISTORY_SORT_OPTIONS();
  public orderBy = signal<string>(ListConstants.PAYMENT_HISTORY_ORG_VIEW_ORDERBY_DEFAULT());

  public summary = signal<PaymentBulkSummaryModel | undefined>(undefined);
  public currency = signal<CurrencyModel | undefined>(undefined);
  public isSummaryLoading = signal<boolean>(true);
  public isDownloading = signal<boolean>(false);

  @ViewChild('paymentList') public paymentList?: PaymentHistoryListComponent;

  public userSessionService = inject(UserSessionService);
  private paymentService = inject(PaymentService);
  private changeDetector = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      const settings = this.userSessionService.activeOrganizationSettings();
      if (settings?.currency) {
        this.currency.set(settings.currency);
      }
    });
  }

  public async ngOnInit(): Promise<void> {
    await this.scopeFilterToActiveOrganization();
    this.orderBy.set(this.userSessionService.paymentHistoryOrderBy);
    await this.loadSummary();
  }

  public onSortChanged(orderBy: string): void {
    if (!orderBy || orderBy === this.orderBy()) return;
    this.orderBy.set(orderBy);
    this.userSessionService.paymentHistoryOrderBy = orderBy;
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.PAYMENT_HISTORY_ORDERBY(),
      orderBy
    );
    this.paymentList?.setSortOrder(orderBy);
  }

  public onListOrderByChanged(orderBy: string): void {
    if (!orderBy || orderBy === this.orderBy()) return;
    this.orderBy.set(orderBy);
    this.userSessionService.paymentHistoryOrderBy = orderBy;
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.PAYMENT_HISTORY_ORDERBY(),
      orderBy
    );
  }

  private async scopeFilterToActiveOrganization(): Promise<void> {
    const activeOrg = this.userSessionService.activeOrganization();
    if (activeOrg) {
      this.userSessionService.paymentHistoryFilter.organizationIds = [activeOrg.organizationId];
    }
    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.userSessionService.paymentHistoryFilter.timeZoneId =
      orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;
  }

  public async loadSummary(): Promise<void> {
    this.isSummaryLoading.set(true);
    try {
      const request = this.buildPaymentsBulkActionRequest();
      const response = await this.paymentService.getPaymentBulkSummary(request);
      if (response.success && response.data) {
        this.summary.set(response.data);
      } else {
        this.summary.set(undefined);
      }
    } finally {
      this.isSummaryLoading.set(false);
      this.changeDetector.detectChanges();
    }
  }

  public async onFilterApplied(): Promise<void> {
    this.persistFilter();
    await Promise.all([
      this.loadSummary(),
      this.paymentList?.loadPaymentHistory(false) ?? Promise.resolve()
    ]);
  }

  public async onSearch(): Promise<void> {
    this.persistFilter();
    await Promise.all([
      this.loadSummary(),
      this.paymentList?.loadPaymentHistory(false) ?? Promise.resolve()
    ]);
  }

  public clearSearch(): void {
    this.userSessionService.paymentHistoryFilter.searchTerm = '';
    this.onSearch();
  }

  public async downloadFilteredPayments(): Promise<void> {
    if (this.isDownloading()) return;
    this.isDownloading.set(true);
    try {
      const request = this.buildPaymentsBulkActionRequest();
      const response = await this.paymentService.exportPaymentsCsv(request);
      if (response.success && response.data) {
        FileDownloadUtilities.downloadBlob(response.data, this.buildFilename());
      }
    } finally {
      this.isDownloading.set(false);
    }
  }

  private buildFilename(): string {
    const dateStr = DateUtilities.format(new Date()).replace(/\//g, '-');
    return `Payments-${dateStr}.csv`;
  }

  /**
   * Builds the PaymentBulkActionRequest for both /bulk/summary and /bulk/export.
   * Filter-only mode for this PR — no row selection. When selection UI is added,
   * extend this helper to split selected rows by isManualPayment into the
   * Stripe (selectedIds/excludedIds) vs Manual (selectedManualPaymentIds/excludedManualPaymentIds) buckets.
   */
  private buildPaymentsBulkActionRequest(): PaymentBulkActionRequest {
    return {
      useFilter: true,
      filter: this.userSessionService.paymentHistoryFilter,
      selectedIds: null,
      excludedIds: null,
      selectedManualPaymentIds: null,
      excludedManualPaymentIds: null,
      note: null
    };
  }

  private persistFilter(): void {
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.PAYMENT_HISTORY_FILTER(),
      this.userSessionService.paymentHistoryFilter
    );
  }

}
